import React, { useState } from 'react';

const ResumeFeedbackPanel = ({ recommendations, missingFields, flaggedIssues }) => {
  const [expandedSections, setExpandedSections] = useState({
    recommendations: true,
    missing: true,
    issues: true
  });

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  const SectionHeader = ({ title, section, icon, count, color }) => (
    <button
      onClick={() => toggleSection(section)}
      className={`w-full flex items-center justify-between p-4 ${color} rounded-lg hover:opacity-90 transition-all`}
    >
      <div className="flex items-center space-x-3">
        {icon}
        <div className="text-left">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {count !== undefined && (
            <p className="text-xs text-gray-600">{count} items</p>
          )}
        </div>
      </div>
      <svg
        className={`w-5 h-5 text-gray-600 transition-transform ${expandedSections[section] ? 'transform rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  return (
    <div className="space-y-4">
      {/* Recommendations */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <SectionHeader
          title="Recommendations"
          section="recommendations"
          count={recommendations?.length || 0}
          color="bg-gradient-to-r from-green-50 to-emerald-50"
          icon={
            <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          }
        />
        {expandedSections.recommendations && (
          <div className="p-6 space-y-3">
            {recommendations && recommendations.length > 0 ? (
              recommendations.map((rec, index) => (
                <div
                  key={index}
                  className="flex items-start p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100 hover:border-green-200 transition-colors"
                >
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-green-500 text-white rounded-full text-xs font-bold mr-3 mt-0.5">
                    {index + 1}
                  </span>
                  <p className="text-gray-800 flex-1">{rec}</p>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>No recommendations at this time</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Missing Fields */}
      {missingFields && missingFields.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <SectionHeader
            title="Missing Information"
            section="missing"
            count={missingFields.length}
            color="bg-gradient-to-r from-orange-50 to-amber-50"
            icon={
              <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            }
          />
          {expandedSections.missing && (
            <div className="p-6 space-y-2">
              {missingFields.map((field, index) => (
                <div
                  key={index}
                  className="flex items-center p-3 bg-orange-50 rounded-lg border border-orange-100"
                >
                  <svg className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-800">{field}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Flagged Issues */}
      {flaggedIssues && flaggedIssues.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <SectionHeader
            title="Flagged Issues"
            section="issues"
            count={flaggedIssues.length}
            color="bg-gradient-to-r from-red-50 to-pink-50"
            icon={
              <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
              </svg>
            }
          />
          {expandedSections.issues && (
            <div className="p-6 space-y-3">
              {flaggedIssues.map((issue, index) => (
                <div
                  key={index}
                  className={`flex items-start p-4 rounded-lg border ${getSeverityColor(issue.severity)}`}
                >
                  <div className="flex-shrink-0 mr-3 mt-0.5">
                    {getSeverityIcon(issue.severity)}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm mb-1">
                      {issue.field || 'General Issue'}
                    </div>
                    <p className="text-sm">
                      {issue.issue}
                    </p>
                  </div>
                  {issue.severity && (
                    <span className={`flex-shrink-0 ml-3 px-2 py-1 text-xs font-medium rounded-full ${
                      issue.severity === 'error' ? 'bg-red-100 text-red-700' :
                      issue.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {issue.severity}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Help Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
        <div className="flex items-start space-x-3">
          <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Need Help?</h4>
            <p className="text-sm text-gray-700 mb-3">
              Focus on the recommendations above to improve your resume score. Address high-priority items first for the biggest impact.
            </p>
            <ul className="text-xs text-gray-600 space-y-1">
              <li className="flex items-center">
                <span className="text-green-500 mr-2">•</span>
                Start with missing information
              </li>
              <li className="flex items-center">
                <span className="text-orange-500 mr-2">•</span>
                Fix flagged issues
              </li>
              <li className="flex items-center">
                <span className="text-blue-500 mr-2">•</span>
                Implement recommendations one by one
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeFeedbackPanel;
